import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { GearSix, Plus, Trash, AirplaneTilt, ArrowsClockwise, UserCircle, X, Buildings, PencilSimple, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function AutoScheduleToggle({ programme = "CI" }) {
  const { api } = useAuth();
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const isPX = programme === "PX";
  const endpoint = isPX ? "/settings/px-auto-schedule" : "/settings/auto-schedule";
  const timeLabel = isPX ? "13:30 UTC (2:30 AM NZT)" : "13:00 UTC (2 AM NZT)";
  useEffect(() => { api.get(endpoint).then(r => setState(r.data)).catch(()=>{}); }, [api, endpoint]);
  const toggle = async () => {
    if (!state || saving) return;
    setSaving(true);
    try {
      const next = !state.enabled;
      const { data } = await api.post(endpoint, { enabled: next });
      setState(data);
      toast.success(`${isPX ? "Air Niugini" : "China Airlines"} automatic schedule ${data.enabled ? `ENABLED (daily ${timeLabel})` : "DISABLED (manual only)"}`);
    } catch { toast.error("Failed to update schedule"); }
    finally { setSaving(false); }
  };
  if (!state) return <div className="mt-4 text-xs text-text-secondary">Loading schedule…</div>;
  return (
    <div className="mt-4 p-4 bg-navy/30 rounded-lg border border-[rgba(255,255,255,0.08)]" data-testid={isPX ? "px-auto-schedule-panel" : "auto-schedule-panel"}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-white font-semibold text-sm">{isPX ? "Air Niugini (PX) Automatic Schedule" : "Automatic SerpAPI Schedule"}</p>
          <p className="text-text-secondary text-xs mt-1">
            {state.enabled
              ? `ON — daily fetch runs automatically at ${timeLabel}${isPX ? " · 4 routes × 9 months × 2 cabins (72 calls)" : ""}`
              : `OFF — ${isPX ? "PX sweep" : "SerpAPI"} only runs when manually triggered via the Refresh button`}
          </p>
        </div>
        <button
          onClick={toggle}
          disabled={saving}
          data-testid={isPX ? "px-auto-schedule-toggle" : "auto-schedule-toggle"}
          className={`relative w-14 h-7 rounded-full transition-colors ${state.enabled ? "bg-ci-blue" : "bg-gray-600"} ${saving ? "opacity-50" : ""}`}
        >
          <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${state.enabled ? "translate-x-8" : "translate-x-1"}`} />
        </button>
      </div>
    </div>
  );
}

function FxRateControl() {
  const { api } = useAuth();
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/zyte-fx").then(r => {
      setState(r.data);
      setDraft(String(r.data.rate));
    }).catch(() => {});
  }, [api]);

  const save = async () => {
    const n = parseFloat(draft);
    if (isNaN(n) || n < 0.5 || n > 5.0) {
      toast.error("Rate must be a number between 0.5 and 5.0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/settings/zyte-fx", { rate: n });
      setState({ ...state, rate: data.rate, is_default: false, mode: "manual", source: "manual",
                 updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success(`USD→NZD rate manually set to ${data.rate}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const useLive = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/settings/zyte-fx/refresh");
      const r = await api.get("/settings/zyte-fx");
      setState(r.data); setDraft(String(r.data.rate));
      toast.success(`Live USD→NZD rate applied: ${data.rate}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Live rate unavailable"); }
    finally { setSaving(false); }
  };

  if (!state) return <div className="mt-3 text-xs text-text-secondary">Loading FX rate…</div>;

  const dirty = parseFloat(draft) !== state.rate;

  return (
    <div className="mt-4 p-4 bg-navy/30 rounded-lg border border-[rgba(255,255,255,0.08)]"
         data-testid="zyte-fx-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <p className="text-white font-semibold text-sm">Zyte / Bright Data USD → NZD rate</p>
          <p className="text-text-secondary text-xs mt-1">
            Trip.com returns USD prices; we multiply by this rate before storing NZD fares.
            {state.mode === "auto"
              ? <span className="text-success"> Live rate (auto-refreshed daily{state.source ? ` from ${state.source}` : ""}{state.rate_date ? `, ${state.rate_date.slice(0, 16)}` : ""}).</span>
              : <span className="text-amber-400"> Manual override active — live refresh paused.</span>}
            {state.is_default && <span className="text-amber-400"> (using default)</span>}
            {state.updated_at && (
              <span className="ml-1 text-text-secondary">
                Last edited {new Date(state.updated_at).toLocaleString("en-NZ")} by {state.updated_by || "?"}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="zyte-fx-input"
            type="number" step="0.0001" min="0.5" max="5.0"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            className="w-28 h-9 bg-card-bg border-[rgba(255,255,255,0.1)] text-white text-sm font-mono"
          />
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="zyte-fx-save"
            className="bg-ci-orange hover:bg-ci-orange/90 text-white h-9 px-4 text-xs disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button onClick={useLive} disabled={saving} variant="outline" data-testid="zyte-fx-use-live"
            className="border-[rgba(255,255,255,0.15)] text-text-secondary hover:text-white h-9 px-3 text-xs">
            Use live rate
          </Button>
        </div>
      </div>
    </div>
  );
}

function SampleDateConfigPanel({ routes }) {
  const { api } = useAuth();
  const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrideDraft, setOverrideDraft] = useState({});
  const [exactOpen, setExactOpen] = useState({});
  // Upcoming 9 travel months for the exact-date editor
  const monthKeys = Array.from({ length: 9 }, (_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + i); return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString("en", { month: "short", year: "numeric" }) }; });
  const setExact = (rid, key, field, value) => setOverrideDraft(d => ({ ...d, [rid]: { ...(d[rid] || {}), exact_dates: { ...((d[rid] || {}).exact_dates || {}), [key]: { ...(((d[rid] || {}).exact_dates || {})[key] || {}), [field]: value } } } }));

  useEffect(() => {
    api.get("/settings/sample-dates").then(r => {
      setCfg(r.data);
      setOverrideDraft(r.data.per_route_overrides || {});
    }).catch(() => {});
  }, [api]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.post("/settings/sample-dates", patch);
      setCfg({ ...cfg, ...data.value, day_of_week_label: data.day_of_week_label, updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success("Sample-date config updated — applies to next refresh");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const applyGlobal = () => save({
    day_of_week: cfg.day_of_week,
    include_saturday_fallback: cfg.include_saturday_fallback,
    return_window_nights: cfg.return_window_nights,
    per_route_overrides: {},  // wipe all per-route overrides
  });

  const saveOverrides = () => {
    try { buildAndSave(); } catch { /* validation toast already shown */ }
  };
  const buildAndSave = () => {
    // Strip empty entries
    const clean = {};
    Object.entries(overrideDraft).forEach(([rid, v]) => {
      if (!v) return;
      const entry = {};
      if (v.day_of_week !== undefined && v.day_of_week !== "" && v.day_of_week !== "default") {
        entry.day_of_week = parseInt(v.day_of_week);
        entry.return_window_nights = parseInt(v.return_window_nights || cfg.return_window_nights);
      }
      const exact = {};
      Object.entries(v.exact_dates || {}).forEach(([k, pair]) => {
        if (pair?.outbound && pair?.return) {
          if (pair.return <= pair.outbound) { toast.error(`${k}: return must be after outbound`); throw new Error("invalid"); }
          exact[k] = { outbound: pair.outbound, return: pair.return };
        }
      });
      if (Object.keys(exact).length) entry.exact_dates = exact;
      if (Object.keys(entry).length) clean[rid] = entry;
    });
    save({ per_route_overrides: clean });
  };

  if (!cfg) return <div className="mt-3 text-xs text-text-secondary">Loading sample-date config…</div>;

  return (
    <div className="mt-4 p-4 bg-navy/30 rounded-lg border border-[rgba(255,255,255,0.08)]" data-testid="sample-date-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p className="text-white font-semibold text-sm">Sample Date Configuration</p>
          <p className="text-text-secondary text-xs mt-1">
            Controls which outbound travel day is sampled by every SerpAPI and Zyte refresh.
            Changes apply on the very next refresh — no restart needed.
            {cfg.updated_at && <span className="ml-1">Last edited {new Date(cfg.updated_at).toLocaleString("en-NZ")} by {cfg.updated_by}</span>}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Day of week selector */}
        <div>
          <Label className="text-text-secondary text-[10px] mb-1 block uppercase tracking-wider">Outbound day</Label>
          <Select
            value={String(cfg.day_of_week)}
            onValueChange={v => setCfg({ ...cfg, day_of_week: parseInt(v) })}
          >
            <SelectTrigger data-testid="sample-dow-select" className="h-9 bg-card-bg border-[rgba(255,255,255,0.1)] text-white text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
              {DOW.map((d, i) => (
                <SelectItem key={i} value={String(i)}>
                  {d}day{i === 4 ? "  (default)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Return window */}
        <div>
          <Label className="text-text-secondary text-[10px] mb-1 block uppercase tracking-wider">Return window</Label>
          <Select
            value={String(cfg.return_window_nights)}
            onValueChange={v => setCfg({ ...cfg, return_window_nights: parseInt(v) })}
          >
            <SelectTrigger data-testid="sample-rw-select" className="h-9 bg-card-bg border-[rgba(255,255,255,0.1)] text-white text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
              <SelectItem value="7">7 nights</SelectItem>
              <SelectItem value="10">10 nights</SelectItem>
              <SelectItem value="14">14 nights</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Saturday fallback */}
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid="sample-sat-fallback"
              checked={cfg.include_saturday_fallback}
              onChange={e => setCfg({ ...cfg, include_saturday_fallback: e.target.checked })}
              className="accent-ci-blue w-4 h-4"
            />
            <span>Also sample Saturday when Friday has no CI flight</span>
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Button
          onClick={applyGlobal}
          disabled={saving}
          data-testid="sample-apply-all"
          className="bg-ci-orange hover:bg-ci-orange/90 text-white h-9 text-xs px-4"
        >
          Apply to all routes
        </Button>
        <button
          onClick={() => setShowOverrides(s => !s)}
          className="text-xs text-ci-blue hover:text-white px-3 py-1.5 rounded-lg border border-[rgba(255,255,255,0.15)]"
          data-testid="sample-toggle-overrides"
        >
          {showOverrides ? "Hide" : "Show"} per-route overrides
          {Object.keys(cfg.per_route_overrides || {}).length > 0 && (
            <span className="ml-1 text-ci-orange font-mono">
              ({Object.keys(cfg.per_route_overrides).length})
            </span>
          )}
        </button>
      </div>

      {showOverrides && (
        <div className="mt-3 p-3 bg-card-bg rounded-lg border border-[rgba(255,255,255,0.08)]">
          <p className="text-text-secondary text-xs mb-2">
            Use this if a specific route needs a different travel day (e.g. AKL→CEB on Sunday only), or set
            <strong className="text-white"> exact dates</strong> per month to mirror a manual search (e.g. 05 Nov → 11 Nov). Exact dates win over the day-of-week rule.
          </p>
          <div className="space-y-2 max-h-[28rem] overflow-y-auto">
            {routes.map(r => {
              const ov = overrideDraft[r.id] || {};
              const exactCount = Object.values(ov.exact_dates || {}).filter(p => p?.outbound && p?.return).length;
              return (
                <div key={r.id} className="text-xs" data-testid={`sample-override-row-${r.destination_code}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-white w-24 shrink-0">{r.origin_code}→{r.destination_code}{r.programme === "PX" && <span className="text-[9px] text-text-secondary ml-1">PX</span>}</span>
                  <Select
                    value={ov.day_of_week !== undefined && ov.day_of_week !== "" ? String(ov.day_of_week) : "default"}
                    onValueChange={v => setOverrideDraft(d => ({ ...d, [r.id]: { ...(d[r.id] || {}), day_of_week: v } }))}
                  >
                    <SelectTrigger data-testid={`sample-override-dow-${r.destination_code}`}
                                   className="h-8 w-28 bg-navy border-[rgba(255,255,255,0.1)] text-white text-xs">
                      <SelectValue placeholder="— default" />
                    </SelectTrigger>
                    <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                      <SelectItem value="default">— default</SelectItem>
                      {DOW.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select
                    value={String(ov.return_window_nights || cfg.return_window_nights)}
                    onValueChange={v => setOverrideDraft(d => ({ ...d, [r.id]: { ...(d[r.id] || {}), return_window_nights: v } }))}
                  >
                    <SelectTrigger className="h-8 w-24 bg-navy border-[rgba(255,255,255,0.1)] text-white text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                      <SelectItem value="7">7 nights</SelectItem>
                      <SelectItem value="10">10 nights</SelectItem>
                      <SelectItem value="14">14 nights</SelectItem>
                    </SelectContent>
                  </Select>
                  <button type="button" onClick={() => setExactOpen(o => ({ ...o, [r.id]: !o[r.id] }))}
                    data-testid={`sample-exact-toggle-${r.destination_code}`}
                    className={`px-2 py-1 rounded border text-[11px] ${exactCount ? "border-ci-orange/50 text-ci-orange" : "border-[rgba(255,255,255,0.15)] text-text-secondary hover:text-white"}`}>
                    Exact dates{exactCount ? ` (${exactCount})` : ""}
                  </button>
                </div>
                {exactOpen[r.id] && (
                  <div className="ml-24 mt-2 mb-2 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={`sample-exact-editor-${r.destination_code}`}>
                    {monthKeys.map(m => { const pair = (ov.exact_dates || {})[m.key] || {}; return (
                      <div key={m.key} className="bg-navy rounded-md p-2 border border-[rgba(255,255,255,0.08)]">
                        <div className="text-[10px] text-text-secondary mb-1">{m.label}</div>
                        <div className="flex items-center gap-1">
                          <input type="date" value={pair.outbound || ""} min={`${m.key}-01`} onChange={e => setExact(r.id, m.key, "outbound", e.target.value)}
                            data-testid={`sample-exact-out-${r.destination_code}-${m.key}`} className="w-full bg-card-bg border border-[rgba(255,255,255,0.1)] rounded px-1 py-0.5 text-[11px] text-white" />
                          <span className="text-text-secondary">→</span>
                          <input type="date" value={pair.return || ""} min={pair.outbound || `${m.key}-01`} onChange={e => setExact(r.id, m.key, "return", e.target.value)}
                            data-testid={`sample-exact-ret-${r.destination_code}-${m.key}`} className="w-full bg-card-bg border border-[rgba(255,255,255,0.1)] rounded px-1 py-0.5 text-[11px] text-white" />
                          {(pair.outbound || pair.return) && <button type="button" onClick={() => setOverrideDraft(d => { const ex = { ...((d[r.id] || {}).exact_dates || {}) }; delete ex[m.key]; return { ...d, [r.id]: { ...(d[r.id] || {}), exact_dates: ex } }; })} className="text-text-secondary hover:text-danger text-xs px-1" title="Clear">×</button>}
                        </div>
                      </div>); })}
                  </div>
                )}
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={saveOverrides} disabled={saving}
                    data-testid="sample-save-overrides"
                    className="bg-ci-orange hover:bg-ci-orange/90 text-white h-8 text-xs px-4">
              Save per-route overrides
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CapacityEditor({ routes, airlines }) {
  const { api } = useAuth();
  const [capacityByRoute, setCapacityByRoute] = useState({});  // route_id -> { direct_carriers, sixth_freedom_carriers, ... }
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);  // route_id currently expanded
  const [editing, setEditing] = useState(null);    // cap_id currently in edit mode
  const [editForm, setEditForm] = useState({});
  const [addingFor, setAddingFor] = useState(null);  // route_id for add mode
  const [addForm, setAddForm] = useState({ airline_iata: "", weekly_flights: "", aircraft_type: "", routing: "Direct", is_sixth_freedom: false, hub_via: "" });

  const loadCapacity = useCallback(async (routesList) => {
    setLoading(true);
    const out = {};
    await Promise.all(routesList.map(async r => {
      try { const res = await api.get(`/routes/${r.id}/capacity`); out[r.id] = res.data; }
      catch { out[r.id] = { direct_carriers: [], sixth_freedom_carriers: [] }; }
    }));
    setCapacityByRoute(out);
    setLoading(false);
  }, [api]);

  useEffect(() => { if (routes.length) loadCapacity(routes); }, [routes, loadCapacity]);

  const startEdit = (c) => {
    setEditing(c.id);
    setEditForm({
      weekly_flights: c.weekly_flights || "",
      aircraft_type: c.aircraft_type || "",
      routing: c.routing || "Direct",
      is_sixth_freedom: !!c.is_sixth_freedom,
      hub_via: c.hub_via || "",
    });
  };

  const saveEdit = async (routeId, capId) => {
    try {
      await api.put(`/routes/${routeId}/capacity/${capId}`, editForm);
      toast.success("Capacity updated");
      setEditing(null);
      await loadCapacity(routes);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const removeCap = async (routeId, capId) => {
    if (!window.confirm("Delete this capacity entry?")) return;
    try {
      await api.delete(`/routes/${routeId}/capacity/${capId}`);
      toast.success("Deleted");
      await loadCapacity(routes);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addCap = async (routeId) => {
    if (!addForm.airline_iata || !addForm.weekly_flights) {
      toast.error("Airline and weekly flights required"); return;
    }
    try {
      await api.post(`/routes/${routeId}/capacity`, { ...addForm, hub_via: addForm.hub_via || null });
      toast.success("Capacity added");
      setAddingFor(null);
      setAddForm({ airline_iata: "", weekly_flights: "", aircraft_type: "", routing: "Direct", is_sixth_freedom: false, hub_via: "" });
      await loadCapacity(routes);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="skeleton h-32 rounded-xl" />;

  return (
    <div className="space-y-3" data-testid="capacity-editor">
      {routes.map(r => {
        const cap = capacityByRoute[r.id] || { direct_carriers: [], sixth_freedom_carriers: [] };
        const allCarriers = [...(cap.direct_carriers || []), ...(cap.sixth_freedom_carriers || [])];
        const isExpanded = expanded === r.id;
        const totalCarriers = allCarriers.length;
        return (
          <div key={r.id} className="bg-navy/30 rounded-lg border border-[rgba(255,255,255,0.06)]">
            <button
              onClick={() => setExpanded(isExpanded ? null : r.id)}
              data-testid={`capacity-route-toggle-${r.destination_code}`}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-white text-sm">{r.origin_code}</span>
                <span className="text-text-secondary">{"\u2192"}</span>
                <span className="font-mono text-white text-sm">{r.destination_code}</span>
                <span className="text-text-secondary text-xs">({r.destination_city})</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-[10px] border-[rgba(255,255,255,0.15)] text-text-secondary">
                  {totalCarriers} carrier{totalCarriers === 1 ? "" : "s"} · {cap.total_weekly_flights || 0} flights/wk
                </Badge>
                <span className="text-text-secondary text-xs">{isExpanded ? "−" : "+"}</span>
              </div>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 pt-1">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid={`capacity-table-${r.destination_code}`}>
                    <thead>
                      <tr className="border-b border-[rgba(255,255,255,0.08)] text-text-secondary">
                        <th className="text-left px-2 py-1.5 font-medium">Carrier</th>
                        <th className="text-left px-2 py-1.5 font-medium">Weekly Flights</th>
                        <th className="text-left px-2 py-1.5 font-medium">Aircraft</th>
                        <th className="text-left px-2 py-1.5 font-medium">Routing</th>
                        <th className="text-left px-2 py-1.5 font-medium">6th Freedom / Via</th>
                        <th className="text-right px-2 py-1.5 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allCarriers.map(c => {
                        const isEditing = editing === c.id;
                        return (
                          <tr key={c.id} className="border-b border-[rgba(255,255,255,0.04)]">
                            <td className="px-2 py-1.5">
                              <span className="font-mono text-white font-medium">{c.airline_iata}</span>
                              <span className="text-text-secondary ml-1 text-[10px]">{c.airline_name}</span>
                            </td>
                            <td className="px-2 py-1.5">
                              {isEditing ? (
                                <Input value={editForm.weekly_flights} onChange={e=>setEditForm(f=>({...f,weekly_flights:e.target.value}))}
                                  data-testid={`cap-edit-weekly-${c.airline_iata}`}
                                  className="h-7 text-xs w-20 bg-card-bg border-[rgba(255,255,255,0.1)] text-white" />
                              ) : <span className="font-mono text-white">{c.weekly_flights}</span>}
                            </td>
                            <td className="px-2 py-1.5">
                              {isEditing ? (
                                <Input value={editForm.aircraft_type} onChange={e=>setEditForm(f=>({...f,aircraft_type:e.target.value}))}
                                  data-testid={`cap-edit-aircraft-${c.airline_iata}`}
                                  className="h-7 text-xs w-32 bg-card-bg border-[rgba(255,255,255,0.1)] text-white" />
                              ) : <span className="text-text-secondary font-mono">{c.aircraft_type || "—"}</span>}
                            </td>
                            <td className="px-2 py-1.5">
                              {isEditing ? (
                                <Select value={editForm.routing} onValueChange={v=>setEditForm(f=>({...f,routing:v}))}>
                                  <SelectTrigger className="h-7 text-xs w-28 bg-card-bg border-[rgba(255,255,255,0.1)] text-white"><SelectValue/></SelectTrigger>
                                  <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                                    <SelectItem value="Direct">Direct</SelectItem>
                                    <SelectItem value="1-stop">1-stop</SelectItem>
                                    <SelectItem value="2-stop">2-stop</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge variant="outline" className={`text-[9px] ${c.is_sixth_freedom?'border-amber-500/40 text-amber-400':'border-emerald-500/40 text-emerald-400'}`}>
                                  {c.routing}
                                </Badge>
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              {isEditing ? (
                                <div className="flex items-center gap-2">
                                  <input type="checkbox" checked={editForm.is_sixth_freedom}
                                    onChange={e=>setEditForm(f=>({...f,is_sixth_freedom:e.target.checked}))}
                                    className="accent-ci-blue"/>
                                  <Input value={editForm.hub_via} onChange={e=>setEditForm(f=>({...f,hub_via:e.target.value}))}
                                    placeholder="hub" className="h-7 text-xs w-16 bg-card-bg border-[rgba(255,255,255,0.1)] text-white" />
                                </div>
                              ) : c.is_sixth_freedom ? (
                                <span className="text-amber-400 font-mono text-[10px]">via {c.hub_via || "—"}</span>
                              ) : <span className="text-text-secondary text-[10px]">—</span>}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              {isEditing ? (
                                <div className="flex justify-end gap-1">
                                  <button onClick={() => saveEdit(r.id, c.id)}
                                    data-testid={`cap-save-${c.airline_iata}`}
                                    className="text-emerald-400 hover:text-emerald-300 p-1"><Check size={14}/></button>
                                  <button onClick={() => setEditing(null)}
                                    className="text-text-secondary hover:text-white p-1"><X size={14}/></button>
                                </div>
                              ) : (
                                <div className="flex justify-end gap-1">
                                  <button onClick={() => startEdit(c)}
                                    data-testid={`cap-edit-btn-${c.airline_iata}`}
                                    className="text-text-secondary hover:text-ci-blue p-1"><PencilSimple size={14}/></button>
                                  <button onClick={() => removeCap(r.id, c.id)}
                                    data-testid={`cap-delete-${c.airline_iata}`}
                                    className="text-text-secondary hover:text-danger p-1"><Trash size={14}/></button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {allCarriers.length === 0 && (
                        <tr><td colSpan={6} className="px-2 py-4 text-center text-text-secondary text-xs">No capacity data yet. Add a carrier below.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Add row */}
                {addingFor === r.id ? (
                  <div className="mt-3 p-3 bg-card-bg rounded-lg border border-[rgba(255,255,255,0.08)]">
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                      <div>
                        <Label className="text-text-secondary text-[10px] mb-1 block">Airline</Label>
                        <Select value={addForm.airline_iata} onValueChange={v=>setAddForm(f=>({...f,airline_iata:v}))}>
                          <SelectTrigger data-testid={`cap-add-airline-${r.destination_code}`} className="h-8 text-xs bg-navy border-[rgba(255,255,255,0.1)] text-white"><SelectValue placeholder="—"/></SelectTrigger>
                          <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                            {airlines.map(a => <SelectItem key={a.iata_code} value={a.iata_code}>{a.iata_code} · {a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-text-secondary text-[10px] mb-1 block">Weekly</Label>
                        <Input value={addForm.weekly_flights} onChange={e=>setAddForm(f=>({...f,weekly_flights:e.target.value}))}
                          placeholder="7" data-testid={`cap-add-weekly-${r.destination_code}`}
                          className="h-8 text-xs bg-navy border-[rgba(255,255,255,0.1)] text-white"/>
                      </div>
                      <div>
                        <Label className="text-text-secondary text-[10px] mb-1 block">Aircraft</Label>
                        <Input value={addForm.aircraft_type} onChange={e=>setAddForm(f=>({...f,aircraft_type:e.target.value}))}
                          placeholder="A350-900" className="h-8 text-xs bg-navy border-[rgba(255,255,255,0.1)] text-white"/>
                      </div>
                      <div>
                        <Label className="text-text-secondary text-[10px] mb-1 block">Routing</Label>
                        <Select value={addForm.routing} onValueChange={v=>setAddForm(f=>({...f,routing:v}))}>
                          <SelectTrigger className="h-8 text-xs bg-navy border-[rgba(255,255,255,0.1)] text-white"><SelectValue/></SelectTrigger>
                          <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                            <SelectItem value="Direct">Direct</SelectItem>
                            <SelectItem value="1-stop">1-stop</SelectItem>
                            <SelectItem value="2-stop">2-stop</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-text-secondary text-[10px] mb-1 block">Via Hub</Label>
                        <div className="flex items-center gap-1.5">
                          <input type="checkbox" checked={addForm.is_sixth_freedom}
                            onChange={e=>setAddForm(f=>({...f,is_sixth_freedom:e.target.checked}))} className="accent-ci-blue"/>
                          <Input value={addForm.hub_via} onChange={e=>setAddForm(f=>({...f,hub_via:e.target.value}))}
                            placeholder="SIN" className="h-8 text-xs bg-navy border-[rgba(255,255,255,0.1)] text-white"/>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" onClick={()=>addCap(r.id)}
                          data-testid={`cap-add-save-${r.destination_code}`}
                          className="bg-ci-orange hover:bg-ci-orange/90 text-white h-8 text-xs px-3">Save</Button>
                        <Button size="sm" variant="outline" onClick={()=>{setAddingFor(null);}}
                          className="border-[rgba(255,255,255,0.15)] text-text-secondary h-8 text-xs px-2">Cancel</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={()=>setAddingFor(r.id)}
                    data-testid={`cap-add-btn-${r.destination_code}`}
                    className="mt-2 flex items-center gap-1.5 text-xs text-ci-blue hover:text-white px-3 py-1.5 rounded-lg border border-dashed border-[rgba(255,255,255,0.15)] hover:border-ci-blue transition-colors"
                  >
                    <Plus size={12}/> Add carrier
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Settings() {
  const { api, user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serpapiStatus, setSerpapiStatus] = useState(null);
  const [routeAirlines, setRouteAirlines] = useState({});
  const [addingAirline, setAddingAirline] = useState({});

  const [newRoute, setNewRoute] = useState({ origin_code: "AKL", origin_city: "Auckland", destination_code: "", destination_city: "" });
  const [newAirline, setNewAirline] = useState({ iata_code: "", name: "", is_our_airline: false });
  const [showAddRoute, setShowAddRoute] = useState(false);
  const [showAddAirline, setShowAddAirline] = useState(false);
  const isAdmin = user?.role === "admin";

  const loadRouteAirlines = useCallback(async (routesList) => {
    const ra = {};
    for (const r of routesList) {
      try { const res = await api.get(`/routes/${r.id}/airlines`); ra[r.id] = res.data; } catch { ra[r.id] = []; }
    }
    setRouteAirlines(ra);
  }, [api]);

  useEffect(() => {
    const promises = [api.get("/routes", { params: { programme: "all" } }), api.get("/airlines"), api.get("/settings/serpapi")];
    if (isAdmin) promises.push(api.get("/users"));
    Promise.all(promises).then(async results => {
      const r = results[0].data; const a = results[1].data;
      setRoutes(r); setAirlines(a); setSerpapiStatus(results[2].data);
      if (results[3]) setUsers(results[3].data);
      await loadRouteAirlines(r);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api, isAdmin, loadRouteAirlines]);

  const addRoute = async (e) => { e.preventDefault(); try { const res = await api.post("/routes", newRoute); setRoutes(prev => [...prev, res.data]); setNewRoute({ origin_code: "AKL", origin_city: "Auckland", destination_code: "", destination_city: "" }); setShowAddRoute(false); toast.success("Route added"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const deleteRoute = async (id) => { if (!window.confirm("Delete this route?")) return; await api.delete(`/routes/${id}`); setRoutes(prev => prev.filter(r => r.id !== id)); toast.success("Route removed"); };
  const addAirline = async (e) => { e.preventDefault(); try { const res = await api.post("/airlines", newAirline); setAirlines(prev => [...prev, res.data]); setNewAirline({ iata_code: "", name: "", is_our_airline: false }); setShowAddAirline(false); toast.success("Airline added"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const deleteAirline = async (id) => { if (!window.confirm("Remove this airline?")) return; await api.delete(`/airlines/${id}`); setAirlines(prev => prev.filter(a => a.id !== id)); toast.success("Airline removed"); };
  const updateRole = async (userId, role) => { try { await api.put(`/users/${userId}/role`, { role }); setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u)); toast.success("Role updated"); } catch { toast.error("Failed"); } };
  const refreshAll = async () => { try { toast.info("Starting full refresh..."); await api.post("/fares/refresh-all"); toast.success("Complete!"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const addRouteAirline = async (routeId, iata) => {
    try {
      const res = await api.post(`/routes/${routeId}/airlines`, { airline_iata: iata, display_order: (routeAirlines[routeId]?.length || 0) + 1 });
      setRouteAirlines(prev => ({ ...prev, [routeId]: res.data }));
      setAddingAirline(prev => ({ ...prev, [routeId]: false }));
      toast.success(`${iata} assigned to route`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeRouteAirline = async (routeId, iata) => {
    try {
      const res = await api.delete(`/routes/${routeId}/airlines/${iata}`);
      setRouteAirlines(prev => ({ ...prev, [routeId]: res.data }));
      toast.success(`${iata} removed from route`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="skeleton h-32 rounded-xl" />)}</div>;

  return (
    <div data-testid="settings-page" className="space-y-6">
      <h1 className="font-syne font-bold text-2xl text-white tracking-tight">Settings</h1>

      {/* API Configuration */}
      <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4"><GearSix size={20} weight="duotone" className="text-ci-blue" /><h2 className="font-syne font-bold text-white">API Configuration</h2></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-navy/50 rounded-lg px-4 py-3"><p className="text-text-secondary text-xs mb-1">SerpAPI Key</p><p className="text-white text-sm font-mono">{serpapiStatus?.has_key ? `Configured (${serpapiStatus.key_preview})` : "Not configured"}</p></div>
          <div className="bg-navy/50 rounded-lg px-4 py-3"><p className="text-text-secondary text-xs mb-1">Monthly API Calls</p><p className="text-white text-sm font-mono">{serpapiStatus?.monthly_fetches || 0}</p></div>
        </div>
        {isAdmin && <AutoScheduleToggle />}
        {isAdmin && <AutoScheduleToggle programme="PX" />}
        {isAdmin && <FxRateControl />}
        {isAdmin && <SampleDateConfigPanel routes={routes} />}
      </div>

      {/* Route Management with Airline Assignments */}
      <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3"><AirplaneTilt size={20} weight="duotone" className="text-ci-orange" /><h2 className="font-syne font-bold text-white">Routes</h2></div>
          {isAdmin && <Button onClick={() => setShowAddRoute(!showAddRoute)} variant="outline" size="sm" data-testid="add-route-btn" className="border-[rgba(255,255,255,0.15)] text-text-secondary hover:text-white h-8 text-xs"><Plus size={14} className="mr-1" /> Add Route</Button>}
        </div>
        {showAddRoute && (
          <form onSubmit={addRoute} className="flex flex-wrap items-end gap-3 mb-4 p-4 bg-navy/50 rounded-lg">
            <div><Label className="text-text-secondary text-xs mb-1 block">Dest Code</Label><Input data-testid="new-route-code" value={newRoute.destination_code} onChange={e => setNewRoute(r => ({ ...r, destination_code: e.target.value }))} placeholder="NRT" className="bg-card-bg border-[rgba(255,255,255,0.1)] text-white h-9 w-24" required /></div>
            <div><Label className="text-text-secondary text-xs mb-1 block">Dest City</Label><Input data-testid="new-route-city" value={newRoute.destination_city} onChange={e => setNewRoute(r => ({ ...r, destination_city: e.target.value }))} placeholder="Tokyo" className="bg-card-bg border-[rgba(255,255,255,0.1)] text-white h-9 w-40" required /></div>
            <Button type="submit" data-testid="save-route-btn" size="sm" className="bg-ci-orange hover:bg-ci-orange/90 text-white h-9">Save</Button>
          </form>
        )}
        <div className="space-y-3">
          {routes.map(r => {
            const ra = routeAirlines[r.id] || [];
            const assignedIatas = ra.map(a => a.airline_iata);
            const availableAirlines = airlines.filter(a => !assignedIatas.includes(a.iata_code));
            return (
              <div key={r.id} className="py-3 px-4 bg-navy/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-white text-sm">{r.origin_code}</span>
                    <span className="text-text-secondary">{"\u2192"}</span>
                    <span className="font-mono text-white text-sm">{r.destination_code}</span>
                    <span className="text-text-secondary text-xs">({r.destination_city})</span>
                  </div>
                  {isAdmin && <button onClick={() => deleteRoute(r.id)} data-testid={`delete-route-${r.destination_code}`} className="text-text-secondary hover:text-danger p-1"><Trash size={16} /></button>}
                </div>
                {/* Assigned Airlines */}
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className="text-text-secondary text-xs mr-1">Airlines:</span>
                  {ra.map(a => (
                    <Badge key={a.airline_iata} variant="outline" data-testid={`route-airline-badge-${r.destination_code}-${a.airline_iata}`}
                      className={`text-xs ${a.is_our_airline ? 'border-ci-blue/50 text-ci-blue bg-ci-blue/10' : 'border-[rgba(255,255,255,0.15)] text-text-secondary'} flex items-center gap-1`}>
                      {a.airline_iata}
                      {isAdmin && !a.is_our_airline && (
                        <button onClick={() => removeRouteAirline(r.id, a.airline_iata)} className="hover:text-danger ml-0.5" data-testid={`remove-ra-${r.destination_code}-${a.airline_iata}`}><X size={10} /></button>
                      )}
                    </Badge>
                  ))}
                  {isAdmin && !addingAirline[r.id] && (
                    <button onClick={() => setAddingAirline(prev => ({...prev, [r.id]: true}))} data-testid={`add-ra-btn-${r.destination_code}`}
                      className="w-6 h-6 rounded-full border border-dashed border-[rgba(255,255,255,0.2)] flex items-center justify-center text-text-secondary hover:text-white hover:border-white/40 transition-colors">
                      <Plus size={10} />
                    </button>
                  )}
                  {isAdmin && addingAirline[r.id] && (
                    <Select onValueChange={(v) => addRouteAirline(r.id, v)}>
                      <SelectTrigger data-testid={`add-ra-select-${r.destination_code}`} className="w-28 bg-navy border-[rgba(255,255,255,0.1)] text-white text-xs h-7"><SelectValue placeholder="Add..." /></SelectTrigger>
                      <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                        {availableAirlines.map(a => <SelectItem key={a.iata_code} value={a.iata_code}>{a.iata_code} - {a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Capacity Management (admin only) */}
      {isAdmin && (
        <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Buildings size={20} weight="duotone" className="text-ci-blue" />
              <h2 className="font-syne font-bold text-white">Route Capacity</h2>
            </div>
            <span className="text-text-secondary text-xs">Weekly flights, aircraft & routing per carrier</span>
          </div>
          <CapacityEditor routes={routes} airlines={airlines} />
        </div>
      )}

      {/* Airline Management */}
      <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-syne font-bold text-white">Airlines</h2>
          {isAdmin && <Button onClick={() => setShowAddAirline(!showAddAirline)} variant="outline" size="sm" data-testid="add-airline-btn" className="border-[rgba(255,255,255,0.15)] text-text-secondary hover:text-white h-8 text-xs"><Plus size={14} className="mr-1" /> Add Airline</Button>}
        </div>
        {showAddAirline && (
          <form onSubmit={addAirline} className="flex flex-wrap items-end gap-3 mb-4 p-4 bg-navy/50 rounded-lg">
            <div><Label className="text-text-secondary text-xs mb-1 block">IATA Code</Label><Input data-testid="new-airline-code" value={newAirline.iata_code} onChange={e => setNewAirline(a => ({ ...a, iata_code: e.target.value }))} placeholder="JL" className="bg-card-bg border-[rgba(255,255,255,0.1)] text-white h-9 w-24" required /></div>
            <div><Label className="text-text-secondary text-xs mb-1 block">Name</Label><Input data-testid="new-airline-name" value={newAirline.name} onChange={e => setNewAirline(a => ({ ...a, name: e.target.value }))} placeholder="Japan Airlines" className="bg-card-bg border-[rgba(255,255,255,0.1)] text-white h-9 w-48" required /></div>
            <Button type="submit" data-testid="save-airline-btn" size="sm" className="bg-ci-orange hover:bg-ci-orange/90 text-white h-9">Save</Button>
          </form>
        )}
        <div className="space-y-2">
          {airlines.map(a => (
            <div key={a.id} className="flex items-center justify-between py-2 px-3 bg-navy/30 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="font-mono text-ci-blue text-sm font-bold">{a.iata_code}</span>
                <span className="text-white text-sm">{a.name}</span>
                {a.is_our_airline && <Badge variant="outline" className="text-xs border-ci-blue/40 text-ci-blue">Our Airline</Badge>}
              </div>
              {isAdmin && !a.is_our_airline && <button onClick={() => deleteAirline(a.id)} data-testid={`delete-airline-${a.iata_code}`} className="text-text-secondary hover:text-danger p-1"><Trash size={16} /></button>}
            </div>
          ))}
        </div>
      </div>

      {/* User Management */}
      {isAdmin && users.length > 0 && (
        <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4"><UserCircle size={20} weight="duotone" className="text-ci-blue" /><h2 className="font-syne font-bold text-white">Users</h2></div>
          <div className="space-y-2">
            {users.map(u => (
              <div key={u.id} className="flex items-center justify-between py-2 px-3 bg-navy/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-ci-blue/20 flex items-center justify-center text-xs font-bold text-white">{u.full_name?.[0] || u.email?.[0]?.toUpperCase()}</div>
                  <div><span className="text-white text-sm">{u.full_name || u.email}</span><span className="text-text-secondary text-xs ml-2">{u.email}</span></div>
                </div>
                <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                  <SelectTrigger data-testid={`user-role-${u.email}`} className="w-32 bg-navy border-[rgba(255,255,255,0.1)] text-white text-xs h-8"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                    <SelectItem value="admin">Admin</SelectItem><SelectItem value="analyst">Analyst</SelectItem><SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
